import { FC, FormEvent, useEffect, useMemo, useState } from 'react'

import { Nomenclature as OrderNomenclature } from 'entities/orders/model'
import {
    NomenclaturesType,
    useNomenclaturesAsyncAutocompleteOptions,
    useNomenclaturesAvailableCountQuery,
} from 'entities/nomenclatures/model'
import { Icon } from 'shared/components/Icons'
import { Autocomplete } from 'shared/components/Autocomplete'
import { Counter } from 'shared/components/Counter'

import {
    StyledAddedNomenclature,
    StyledAddedNomenclatureContainer,
    StyledAutocompleteWrapper,
    StyledContainer,
    StyledCrossIconContainer,
    StyledNomenclatureInputContainer,
} from './styles'
import { NomenclaturesInputProps } from './types'
import { adaptNomenclaturesToOption, NomenclaturesOptions } from './utils'
import { NomenclatureLabel } from './NomenclatureLabel'
import { DiscountInputContainer } from './DiscountInputContainer'
import { getBookingRange } from 'features/OrderPreview/ui/OrderTotalPricePreview/utils'

export const NomenclaturesInput: FC<NomenclaturesInputProps> = ({
    booking,
    disabled,
    values,
    onAddNomenclature,
    onEditNomenclature,
    onRemoveNomenclature,
}) => {
    const [nomenclatureToEdit, setNomenclatureToEdit] =
        useState<OrderNomenclature | null>(null)

    const [selectedValue, setSelectedValue] =
        useState<NomenclaturesOptions | null>()
    const [count, setCount] = useState<number>(0)
    const [price, setPrice] = useState<number | null>(null)
    const [absolutDiscount, setAbsolutDiscount] = useState<number | null>(null)
    const [percentDiscount, setPercentDiscount] = useState<number | null>(null)

    const isValid = !!selectedValue && count > 0

    const bookingRange = useMemo(
        () => (booking ? getBookingRange(booking).diff : 0),
        [booking],
    )

    const [searchString, setSearchString] = useState<string>()
    const { nomenclatures, fetchNextPage, isFetching, refetch } =
        useNomenclaturesAsyncAutocompleteOptions({
            search_param: searchString,
            size: 100,
        })

    const { currentCount, isFetching: isFetchingCurrentCount } = useNomenclaturesAvailableCountQuery({
        id: selectedValue?.value,
        start: booking?.startTime,
        end: booking?.endTime,
    })

    const countLimit =
        selectedValue?.payload?.type === NomenclaturesType.AdditionalService
            ? (currentCount ?? 0) > 0 ? bookingRange : 0
            : currentCount

    useEffect(() => {
        if (!nomenclatureToEdit) {
            return
        }

        const option = options.find(
            n =>
                n.value.toString() ===
                nomenclatureToEdit.nomenclatureId?.toString(),
        )
        setSelectedValue(option)
        setCount(nomenclatureToEdit?.count ?? 0)
        setPrice(nomenclatureToEdit?.price ?? 0)
        setAbsolutDiscount(nomenclatureToEdit?.absolutDiscount ?? 0)
        setPercentDiscount(nomenclatureToEdit?.percentDiscount ?? 0)
    }, [nomenclatureToEdit])

    const currentNomenclature = useMemo(
        () =>
            nomenclatures?.find(
                nomenclature =>
                    nomenclature.id.toString() === selectedValue?.value,
            ),
        [nomenclatures, selectedValue],
    )

    const options: NomenclaturesOptions[] =
        nomenclatures
            ?.filter(nomenclature => nomenclature.count >= 1)
            ?.map(adaptNomenclaturesToOption) ?? []

    const onChangeSelectedValue = (option: NomenclaturesOptions) => {
        setSelectedValue(option)
        setNomenclatureToEdit(null)
        setAbsolutDiscount(null)
        setPercentDiscount(null)
        setPrice(null)

        if (!option) {
            setCount(0)
            return
        }
    }

    useEffect(() => {
        if (!selectedValue) {
            return
        }

        setCount((countLimit ?? 0) > 0 ? 1 : 0)
    }, [countLimit, selectedValue])

    useEffect(() => {
        if (currentNomenclature) {
            setPrice(nomenclatureToEdit?.price ?? currentNomenclature.price)
        }
    }, [currentNomenclature])

    const onRemoveSelectedValue = async (option: OrderNomenclature) => {
        if (onRemoveNomenclature) {
            onRemoveNomenclature(option)
        }
    }

    const onIncreaseCount = () => {
        setCount(prev => Math.min(prev + 1, (countLimit ?? 0)))
    }

    const onDecreaseCount = () => {
        setCount(prev => Math.max(prev - 1, 0))
    }

    const onPriceChange = (e: FormEvent) => {
        if (!currentNomenclature) {
            return
        }

        const newPrice = Math.abs(Number((e.target as HTMLInputElement).value))

        if (Number.isNaN(newPrice)) {
            return
        }

        setPrice(newPrice)
    }

    const onAbsolutDiscountChange = (e: FormEvent) => {
        if (!currentNomenclature) {
            return
        }

        const newAbsolutDiscount = Math.abs(
            Number((e.target as HTMLInputElement).value),
        )
        if (Number.isNaN(newAbsolutDiscount)) {
            return
        }

        setAbsolutDiscount(
            Math.min(newAbsolutDiscount, currentNomenclature.price),
        )
        setPercentDiscount(null)
    }

    const onPercentDiscountChange = (e: FormEvent) => {
        if (!currentNomenclature) {
            return
        }

        const newPercentDiscount = Math.abs(
            Number((e.target as HTMLInputElement).value),
        )
        if (Number.isNaN(newPercentDiscount)) {
            return
        }
        setPercentDiscount(Math.min(newPercentDiscount, 100))
        setAbsolutDiscount(null)
    }

    const onAdd = async () => {
        setCount(0)
        setAbsolutDiscount(null)
        setPercentDiscount(null)
        setSelectedValue(null)
        setPrice(null)

        if (onEditNomenclature && nomenclatureToEdit && selectedValue) {
            onEditNomenclature({
                id: Number(nomenclatureToEdit.id),
                name: selectedValue.label,
                count,
                percentDiscount: percentDiscount ?? 0,
                absolutDiscount: absolutDiscount ?? 0,
                price: price ?? currentNomenclature?.price ?? null,
                type: 0,
            })
            setNomenclatureToEdit(null)
            refetch().then()
            return
        }
        if (onAddNomenclature && selectedValue) {
            onAddNomenclature({
                id: Number(selectedValue.value),
                name: selectedValue.label,
                count,
                percentDiscount: percentDiscount ?? 0,
                absolutDiscount: absolutDiscount ?? 0,
                price: price ?? currentNomenclature?.price ?? null,
                type: 0,
            })
            refetch().then()
        }
    }

    return (
        <StyledContainer>
            <StyledNomenclatureInputContainer>
                <StyledAutocompleteWrapper>
                    <Autocomplete
                        options={options}
                        onChange={onChangeSelectedValue as any}
                        value={selectedValue}
                        placeholder='-'
                        disabled={disabled}
                        isLoading={isFetching || isFetchingCurrentCount}
                        onInputChange={setSearchString}
                        onLoadMore={fetchNextPage}
                    />
                </StyledAutocompleteWrapper>

                <Counter
                    disabled={disabled}
                    count={count}
                    onDecreaseCount={onDecreaseCount}
                    onIncreaseCount={onIncreaseCount}
                    disabledIncrease={count === countLimit}
                    disabledDecrease={count === 0}
                />
            </StyledNomenclatureInputContainer>

            <DiscountInputContainer
                isShowPriceInput
                price={price}
                onPriceChange={onPriceChange}
                absolutDiscount={absolutDiscount}
                onAbsolutDiscountChange={onAbsolutDiscountChange}
                percentDiscount={percentDiscount}
                onPercentDiscountChange={onPercentDiscountChange}
                onAdd={onAdd}
                disabledAddButton={!isValid}
            />
            <StyledAddedNomenclatureContainer>
                {(values ?? []).map((val, index) => (
                    <StyledAddedNomenclature
                        key={index}
                        onClick={() => setNomenclatureToEdit(val)}
                        selected={val.id === nomenclatureToEdit?.id}
                    >
                        <NomenclatureLabel nomenclature={val} />
                        {!disabled && (
                            <StyledCrossIconContainer
                                onClick={() => onRemoveSelectedValue(val)}
                            >
                                <Icon name='deleteRoundedIcon' />
                            </StyledCrossIconContainer>
                        )}
                    </StyledAddedNomenclature>
                ))}
            </StyledAddedNomenclatureContainer>
        </StyledContainer>
    )
}
